import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  IconButton,
  Menu,
  MenuItem,
  Tab,
  Tabs,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Snackbar,
  Avatar,
  Paper,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import BusinessIcon from '@mui/icons-material/Business';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { getDepartments, getDepartmentEmployees, bulkUploadDepartments } from '../../../../api/departmentApi';
import { useErrorModal } from '../../../../hooks/useErrorModal';
import PermissionGuard from '../../../../components/PermissionGuard';
import BulkUploadModal from '../../../../components/common/BulkUploadModal';

const getInitials = (name) => {
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  const initials = parts.slice(0, 2).map((p) => p[0]?.toUpperCase()).join('');
  return initials || 'D';
};

const filterInputStyle = {
  borderRadius: '10px',
  backgroundColor: '#fff',
  transition: 'all 0.2s ease-in-out',
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: 'rgba(226, 232, 240, 0.8)',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: 'rgba(117, 34, 209, 0.4)',
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: '#7522D1',
  },
};

const stringToColor = (string) => {
  if (!string) return '#7522D1';
  let hash = 0;
  for (let i = 0; i < string.length; i += 1) {
    hash = string.charCodeAt(i) + ((hash << 5) - hash);
  }
  let color = '#';
  for (let i = 0; i < 3; i += 1) {
    const value = (hash >> (i * 8)) & 0xff;
    color += `00${value.toString(16)}`.slice(-2);
  }
  return color;
};

const Department = () => {
  const navigate = useNavigate();
  const [departments, setDepartments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [selectedDepartmentId, setSelectedDepartmentId] = useState('');
  const [detailsTab, setDetailsTab] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [employeeSearchQuery, setEmployeeSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [employeesLoading, setEmployeesLoading] = useState(false);
  const [error, setError] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const { showError, ErrorModal } = useErrorModal();

  const filteredDepartments = useMemo(() => {
    return departments.filter((d) => d.name.toLowerCase().includes(searchQuery.toLowerCase()));
  }, [departments, searchQuery]);

  const selectedDepartment = useMemo(() => {
    return departments.find((d) => d.id === selectedDepartmentId);
  }, [departments, selectedDepartmentId]);

  const filteredEmployees = useMemo(() => {
    return employees.filter((e) => {
      const fullName = `${e.firstName || ''} ${e.lastName || ''}`.toLowerCase();
      const empId = String(e.employeeId || '').toLowerCase();
      const search = employeeSearchQuery.toLowerCase();
      return fullName.includes(search) || empId.includes(search);
    });
  }, [employees, employeeSearchQuery]);

  const handleAddClick = () => {
    navigate('/org-management/org-structure/add-department');
  };

  const handleMenuClick = (event, department) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    setSelectedDepartmentId(department.id);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const handleEdit = () => {
    if (selectedDepartment) {
      navigate(`/org-management/org-structure/edit-department/${selectedDepartment.id}`, {
        state: { departmentData: selectedDepartment }
      });
    }
    handleMenuClose();
  };

  const fetchDepartments = async () => {
    try {
      setLoading(true);
      const result = await getDepartments();
      if (result.success) {
        setDepartments(result.data);
        if (result.data.length > 0 && !selectedDepartmentId) {
          setSelectedDepartmentId(result.data[0].id);
        }
      } else {
        setError(result.message);
      }
    } catch (error) {
      showError(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDepartmentEmployees = async (departmentId) => {
    if (!departmentId) return;

    try {
      setEmployeesLoading(true);
      const result = await getDepartmentEmployees(departmentId);
      if (result.success) {
        setEmployees(result.data);
      } else {
        setSnackbar({ open: true, message: result.message, severity: 'error' });
      }
    } catch (error) {
      showError(error);
    } finally {
      setEmployeesLoading(false);
    }
  };

  useEffect(() => {
    fetchDepartments();
  }, []);

  useEffect(() => {
    if (selectedDepartmentId) {
      fetchDepartmentEmployees(selectedDepartmentId);
    }
  }, [selectedDepartmentId]);

  const handleBulkUpload = async (formData) => {
    const result = await bulkUploadDepartments(formData);
    if (result.success) {
      setSnackbar({ open: true, message: result.message, severity: 'success' });
      fetchDepartments();
      setIsUploadOpen(false);
    } else {
      throw new Error(result.message || 'Upload failed');
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <Box sx={{ width: '100%', marginTop: 0 }}>
      <Box
        sx={{
          backgroundColor: '#FFFFFF',
          padding: '20px 24px',
          marginBottom: 3,
          borderRadius: '12px',
          border: '1px solid rgba(117, 34, 209, 0.08)',
          borderLeft: '6px solid #7522D1',
          boxShadow: '0 4px 20px rgba(117, 34, 209, 0.04)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Box>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: '#030007',
              marginBottom: 0.5,
              fontSize: '1.1rem',
            }}
          >
            Departments
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: '#4F4937',
              fontSize: '0.85rem',
            }}
          >
            Manage your organization's departments
          </Typography>
        </Box>
        <PermissionGuard module="departments" action="create">
          <Box sx={{ display: 'flex' }}>
            <Button
              variant="outlined"
              startIcon={<CloudUploadIcon />}
              onClick={() => setIsUploadOpen(true)}
              sx={{
                borderColor: 'rgba(117, 34, 209, 0.5)',
                color: '#7522D1',
                textTransform: 'none',
                fontWeight: 600,
                paddingX: 2,
                paddingY: 1,
                borderRadius: '10px',
                mr: 2,
                whiteSpace: 'nowrap',
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  borderColor: '#7522D1',
                  backgroundColor: 'rgba(117, 34, 209, 0.04)',
                  transform: 'translateY(-1px)',
                },
              }}
            >
              Upload Excel
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddClick}
              sx={{
                backgroundColor: '#7522D1',
                color: '#FFFFFF',
                textTransform: 'none',
                fontWeight: 600,
                paddingX: 2.5,
                paddingY: 1.2,
                borderRadius: '10px',
                whiteSpace: 'nowrap',
                boxShadow: '0 4px 12px rgba(117, 34, 209, 0.2)',
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  backgroundColor: '#5E1BA8',
                  boxShadow: '0 6px 16px rgba(117, 34, 209, 0.3)',
                  transform: 'translateY(-1px)',
                },
              }}
            >
              Add Department
            </Button>
          </Box>
        </PermissionGuard>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
        {/* Sidebar */}
        <Box sx={{ width: { xs: '100%', md: '30%' }, minWidth: { xs: '100%', md: 300 } }}>
          <TextField
            fullWidth
            placeholder="Search here"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: <SearchIcon sx={{ marginRight: 1, color: '#7522D1', fontSize: '1.2rem' }} />,
            }}
            variant="outlined"
            sx={{
              marginBottom: 2,
              '& .MuiOutlinedInput-root': filterInputStyle,
            }}
          />

          <Typography
            sx={{
              fontWeight: 600,
              color: '#030007',
              marginBottom: 2,
              fontSize: '0.9rem',
            }}
          >
            Departments ({filteredDepartments.length})
          </Typography>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
              <CircularProgress sx={{ color: '#7522D1' }} />
            </Box>
          ) : error ? (
            <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
          ) : (
            filteredDepartments.map((department) => (
              <Card
                key={department.id}
                onClick={() => setSelectedDepartmentId(department.id)}
                sx={{
                  padding: 2,
                  marginBottom: 1.5,
                  cursor: 'pointer',
                  backgroundColor: selectedDepartmentId === department.id ? '#F5EEFC' : '#FFFFFF',
                  border: selectedDepartmentId === department.id ? '1px solid #7522D1' : '1px solid rgba(117, 34, 209, 0.08)',
                  borderLeft: selectedDepartmentId === department.id ? '6px solid #7522D1' : '1px solid rgba(117, 34, 209, 0.08)',
                  borderRadius: '12px',
                  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                  boxShadow: selectedDepartmentId === department.id ? '0 4px 12px rgba(117, 34, 209, 0.08)' : 'none',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 6px 20px rgba(117, 34, 209, 0.12)',
                    borderColor: '#7522D1',
                  },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '10px',
                      backgroundColor: stringToColor(department.name),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      color: '#FFFFFF',
                      fontWeight: 700,
                      fontSize: '0.9rem',
                    }}
                  >
                    {getInitials(department.name)}
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      sx={{
                        fontWeight: 600,
                        color: '#030007',
                        fontSize: '0.95rem',
                      }}
                    >
                      {department.name}
                    </Typography>
                    <Typography
                      sx={{
                        color: '#4F4937',
                        fontSize: '0.85rem',
                      }}
                    >
                      {department.employeeCount || 0} Employees
                    </Typography>
                  </Box>
                  <IconButton onClick={(e) => handleMenuClick(e, department)} size="small">
                    <MoreVertIcon sx={{ color: '#4F4937' }} />
                  </IconButton>
                </Box>
              </Card>
            ))
          )}
        </Box>

        {/* Main Content */}
        <Box sx={{ flex: 1 }}>
          {selectedDepartment ? (
            <Card
              sx={{
                padding: 4,
                boxShadow: '0 4px 20px rgba(117, 34, 209, 0.02)',
                border: '1px solid rgba(117, 34, 209, 0.08)',
                borderRadius: '16px',
                backgroundColor: '#FFFFFF',
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: '12px',
                      backgroundColor: stringToColor(selectedDepartment.name),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#FFFFFF',
                      fontWeight: 700,
                      fontSize: '1.2rem',
                    }}
                  >
                    {getInitials(selectedDepartment.name)}
                  </Avatar>
                  <Box>
                    <Typography
                      sx={{
                        fontWeight: 650,
                        color: '#030007',
                        fontSize: '1.1rem',
                      }}
                    >
                      {selectedDepartment.name}
                    </Typography>
                    <Typography
                      sx={{
                        color: '#4F4937',
                        fontSize: '0.9rem',
                        fontWeight: 500,
                      }}
                    >
                      {selectedDepartment.employeeCount || 0} Employees
                    </Typography>
                    <Typography
                      sx={{
                        color: '#4F4937',
                        fontSize: '0.85rem',
                      }}
                    >
                      {selectedDepartment.location || 'Not specified'}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              <Tabs
                value={detailsTab}
                onChange={(_, v) => setDetailsTab(v)}
                sx={{
                  borderBottom: '1px solid rgba(117, 34, 209, 0.08)',
                  marginBottom: 3,
                  '& .MuiTabs-indicator': {
                    backgroundColor: '#7522D1',
                    height: '3px',
                    borderRadius: '3px 3px 0 0',
                  },
                }}
              >
                <Tab
                  label="Summary"
                  sx={{
                    textTransform: 'none',
                    fontSize: '0.95rem',
                    color: '#4F4937',
                    fontWeight: detailsTab === 0 ? 600 : 400,
                    '&.Mui-selected': {
                      color: '#7522D1',
                    },
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      color: '#7522D1',
                    },
                  }}
                />
                <Tab
                  label="Employees"
                  sx={{
                    textTransform: 'none',
                    fontSize: '0.95rem',
                    color: '#4F4937',
                    fontWeight: detailsTab === 1 ? 600 : 400,
                    '&.Mui-selected': {
                      color: '#7522D1',
                    },
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      color: '#7522D1',
                    },
                  }}
                />
              </Tabs>

              {detailsTab === 0 && (
                <Box>
                  <Typography
                    sx={{
                      fontWeight: 600,
                      color: '#030007',
                      marginBottom: 1,
                      fontSize: '0.9rem',
                    }}
                  >
                    Description
                  </Typography>
                  <Typography
                    sx={{
                      color: '#4F4937',
                      fontSize: '0.9rem',
                      marginBottom: 3,
                    }}
                  >
                    {selectedDepartment.description || 'No description available'}
                  </Typography>
                </Box>
              )}

              {detailsTab === 1 && (
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <TextField
                      size="small"
                      placeholder="Search here"
                      value={employeeSearchQuery}
                      onChange={(e) => setEmployeeSearchQuery(e.target.value)}
                      InputProps={{
                        startAdornment: <SearchIcon sx={{ marginRight: 1, color: '#7522D1', fontSize: '1.1rem' }} />,
                      }}
                      variant="outlined"
                      sx={{
                        width: 250,
                        '& .MuiOutlinedInput-root': filterInputStyle,
                      }}
                    />
                    <Box sx={{ flex: 1 }} />
                  </Box>

                  <TableContainer
                    component={Paper}
                    elevation={0}
                    sx={{
                      border: '1px solid rgba(117, 34, 209, 0.08)',
                      borderRadius: '12px',
                      overflow: 'hidden',
                    }}
                  >
                    <Table size="medium">
                      <TableHead sx={{ backgroundColor: '#F8F5FC' }}>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600, color: '#42374F', fontSize: '0.85rem', paddingY: 1.5 }}>EMPLOYEE NUMBER</TableCell>
                          <TableCell sx={{ fontWeight: 600, color: '#42374F', fontSize: '0.85rem', paddingY: 1.5 }}>EMPLOYEE NAME</TableCell>
                          <TableCell sx={{ fontWeight: 600, color: '#42374F', fontSize: '0.85rem', paddingY: 1.5 }}>JOB TITLE</TableCell>
                          <TableCell sx={{ fontWeight: 600, color: '#42374F', fontSize: '0.85rem', paddingY: 1.5 }}>REPORTING TO</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {employeesLoading ? (
                          <TableRow>
                            <TableCell colSpan={4} sx={{ textAlign: 'center', py: 4 }}>
                              <CircularProgress size={24} sx={{ color: '#7522D1' }} />
                            </TableCell>
                          </TableRow>
                        ) : filteredEmployees.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={4} sx={{ textAlign: 'center', py: 4, color: '#4F4937' }}>
                              No employees found
                            </TableCell>
                          </TableRow>
                        ) : (
                          filteredEmployees.map((e) => (
                            <TableRow
                              key={e.id}
                              sx={{
                                '&:hover': { backgroundColor: 'rgba(117, 34, 209, 0.02)' },
                                transition: 'all 0.15s ease-in-out',
                              }}
                            >
                              <TableCell sx={{ paddingY: 1.5, color: '#030007', fontWeight: 500 }}>{e.employeeId}</TableCell>
                              <TableCell sx={{ paddingY: 1.5 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                  <Avatar
                                    sx={{
                                      width: 32,
                                      height: 32,
                                      fontSize: '0.85rem',
                                      fontWeight: 600,
                                      backgroundColor: stringToColor(`${e.firstName} ${e.lastName}`),
                                      color: '#FFFFFF',
                                    }}
                                  >
                                    {getInitials(`${e.firstName} ${e.lastName}`)}
                                  </Avatar>
                                  <Typography sx={{ fontSize: '0.9rem', fontWeight: 500, color: '#030007' }}>
                                    {`${e.firstName} ${e.lastName}`}
                                  </Typography>
                                </Box>
                              </TableCell>
                              <TableCell sx={{ paddingY: 1.5, color: '#4F4937' }}>{e.designation?.name || 'N/A'}</TableCell>
                              <TableCell sx={{ paddingY: 1.5, color: '#4F4937' }}>
                                {e.manager ? (
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Avatar
                                      sx={{
                                        width: 24,
                                        height: 24,
                                        fontSize: '0.75rem',
                                        fontWeight: 600,
                                        backgroundColor: stringToColor(`${e.manager.firstName} ${e.manager.lastName}`),
                                        color: '#FFFFFF',
                                      }}
                                    >
                                      {getInitials(`${e.manager.firstName} ${e.manager.lastName}`)}
                                    </Avatar>
                                    <Typography sx={{ fontSize: '0.85rem', color: '#4F4937' }}>
                                      {`${e.manager.firstName} ${e.manager.lastName}`}
                                    </Typography>
                                  </Box>
                                ) : 'N/A'}
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}
            </Card>
          ) : (
            <Card
              sx={{
                padding: 4,
                boxShadow: '0 4px 20px rgba(117, 34, 209, 0.02)',
                border: '1px solid rgba(117, 34, 209, 0.08)',
                borderRadius: '16px',
                textAlign: 'center',
                backgroundColor: '#FFFFFF',
              }}
            >
              <Typography color="text.secondary">Select a department to view details</Typography>
            </Card>
          )}
        </Box>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{
            width: '100%',
            borderRadius: '12px',
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.08)',
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          elevation: 0,
          sx: {
            mt: 1.5,
            minWidth: 160,
            borderRadius: '12px',
            border: '1px solid rgba(117, 34, 209, 0.08)',
            boxShadow: '0 10px 30px rgba(117, 34, 209, 0.08)',
            overflow: 'visible',
            '& .MuiMenuItem-root': {
              fontSize: '0.9rem',
              padding: '10px 16px',
              color: '#42374F',
              borderRadius: '8px',
              margin: '4px 8px',
              transition: 'all 0.15s ease-in-out',
              '&:hover': {
                backgroundColor: 'rgba(117, 34, 209, 0.05)',
                color: '#7522D1',
              },
            },
          },
        }}
      >
        <PermissionGuard module="departments" action="edit">
          <MenuItem onClick={handleEdit}>
            <EditIcon sx={{ mr: 1.5, fontSize: 18 }} />
            Edit
          </MenuItem>
        </PermissionGuard>
      </Menu>

      <ErrorModal />
      <BulkUploadModal
        open={isUploadOpen}
        onClose={() => setIsUploadOpen(false)}
        title="Upload Departments"
        onUpload={handleBulkUpload}
      />
    </Box>
  );
};

export default Department;
